import { useRef, useState } from 'preact/hooks'
import { CloudUpload } from 'lucide-preact'
import { WAITING_IMAGE_ACCEPT, WAITING_IMAGE_LIMITS } from './roomWaiting'
import { LINK_BUTTON } from './roomStyles'

interface ImageDropZoneProps {
    disabled: boolean
    onFile: (file: File) => void
}

/**
 * Where the While viewers wait card's image is dropped or chosen (ORB-1118). Its own, not the video's `DropZone`: an
 * image comes from the computer alone, as Drive, OneDrive and Dropbox stay video only.
 */
export function ImageDropZone({ disabled, onFile }: ImageDropZoneProps) {
    const [over, setOver] = useState(false)
    const input = useRef<HTMLInputElement>(null)
    return (
        <div
            className={`flex flex-col items-center text-center gap-1 px-6 pt-8 pb-7 rounded-xl border-[1.5px] border-dashed transition-colors ${over
                ? 'border-primary-400 bg-primary-50 dark:bg-primary-900/20'
                : 'border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-900/40'}`}
            onDragOver={event => { event.preventDefault(); if (!disabled) setOver(true) }}
            onDragLeave={event => { if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setOver(false) }}
            onDrop={event => {
                event.preventDefault()
                setOver(false)
                const file = event.dataTransfer?.files?.[0]
                if (file && !disabled) onFile(file)
            }}
        >
            <span className="w-11 h-11 mb-2.5 rounded-full grid place-items-center bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-400">
                <CloudUpload className="w-5 h-5" />
            </span>
            <p className="text-[15px] font-medium text-slate-900 dark:text-white">
                Drag the image here, or{' '}
                <button type="button" className={LINK_BUTTON} disabled={disabled} onClick={() => input.current?.click()}>choose a file</button>
            </p>
            <p className="text-sm text-slate-500 dark:text-slate-400 max-w-[52ch]">{WAITING_IMAGE_LIMITS}</p>
            {/* The zone is the control, as the video's drop zone is, so its own picker stays hidden. */}
            <input
                ref={input}
                type="file"
                accept={WAITING_IMAGE_ACCEPT}
                className="hidden"
                aria-hidden="true"
                tabIndex={-1}
                onChange={event => {
                    const target = event.currentTarget
                    const file = target.files?.[0]
                    target.value = ''
                    if (file) onFile(file)
                }}
            />
        </div>
    )
}
